import { apiAnalytics } from "@/api/analytics/analytics.api.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { TimeOffSettingsTabIndex } from "@/pages/settings/time-off/index/components/TimeOffSettingsTabIndex.tsx";
import { TimeOffSettingsTabPolicyReport } from "@/pages/settings/time-off/index/components/TimeOffSettingsTabPolicyReport.tsx";
import {
  stripEmptyArrays,
  usePolicyReportFilters,
} from "@/pages/settings/time-off/index/time-off-policy-report.filters.ts";
import { TimeOffSettingsTabs } from "@/pages/settings/time-off/index/time-off-settings.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAnalyticsTimeOffLeavePolicyFilter } from "@/types/analytics/analytics.time-off.types.ts";
import { AnalyticReportTypes, ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  Stack,
  TabItem,
  Tabs,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export interface Props {
  tab: TimeOffSettingsTabs;
}

export function TimeOffSettings({ tab }: Props) {
  const { t } = useTranslation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const navigate = useNavigate();

  useTitle(t("time_off:time_off"), t("common:settings"));

  const isEmployeesTab = tab === TimeOffSettingsTabs.policyReport;

  const handleTabChanged = (tabIndex: number) => {
    const urls = ["/settings/time-off", "/settings/time-off/policy-report"];

    navigate(urls[tabIndex]);
    setCurrentPath(urls[tabIndex]);
  };

  const { canIEdit } = usePermissions();
  const {
    filterButton,
    filter: { filter },
  } = useAppFilter<ApiAnalyticsTimeOffLeavePolicyFilter>({
    filters: usePolicyReportFilters(),
    source: AnalyticReportTypes.timeOffPolicy,
  });

  const { isExporting, downloadFile } = useDownloadHelper();
  const handleExportClick = useCallback(
    (format: ApiExportFormat) => {
      const filterToExport: ApiAnalyticsTimeOffLeavePolicyFilter = {
        ...stripEmptyArrays(filter),
        format: format,
      };

      downloadFile({
        apiCall: () => apiAnalytics.timeOff.policyReportExport(filterToExport),
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.dataExported, {
            [analyticProperties.title]: AnalyticReportTypes.timeOffPolicy,
          });
        },
      });
    },
    [filter]
  );

  return (
    <>
      <Page className={isEmployeesTab ? "h-full" : undefined}>
        <Page.Header
          title={t("time_off:settings_title")}
          showBack={true}
          navigateTo={"/settings/policies"}
        >
          {currentPath === "/settings/time-off/policy-report" && (
            <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
              {canIEdit(ApiPermission.reporting_export) && (
                <DropdownMenu
                  trigger={
                    <Button isLoading={isExporting} variant={"secondary"}>
                      {`${t("common:export")}...`}
                    </Button>
                  }
                >
                  <DropdownMenuItem
                    onClick={() => handleExportClick(ApiExportFormat.xlsx)}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
                    title={t("common:export_to_xlsx")}
                  />
                </DropdownMenu>
              )}
              {filterButton()}
            </Stack>
          )}
        </Page.Header>
        <Page.Content stretch={isEmployeesTab}>
          <Stack className={isEmployeesTab ? "px-16 flex-1 min-h-0 min-w-0" : "px-16"}>
            <Tabs
              fill={isEmployeesTab}
              onActiveTabChange={handleTabChanged}
              activeTab={tab == TimeOffSettingsTabs.settings ? 0 : 1}
            >
              <TabItem title={t("common:settings")}>
                <TimeOffSettingsTabIndex />
              </TabItem>
              <TabItem
                title={t("core:employees")}
                className={"flex min-h-0 min-w-0 flex-1 flex-col"}
              >
                <TimeOffSettingsTabPolicyReport />
              </TabItem>
            </Tabs>
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
}
